import React, { useRef, useState, useCallback } from "react";
import {
    DmsPage,
    DmsButtonGroup,
    DmsTable,
    DmsAltBox,
    DmsButton,
    DmsPermissions,
    DmsModal,
    DmsSelect,
    DmsTextField,
    fetchData,
    fetchUrl,
    HTTP_STATUS,
    intl
} from "dmsbasereact";
import { Typography, Box, Chip, IconButton, Tooltip, Grid, Alert } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import Swal from "sweetalert2";

import { Permissions } from "../../constants/Permissions";
import { ReturnStatusEnum, getReturnStatusOptions, ReturnStatus } from "../../constants/ReturnStatus";
import { useQualityIssueOptions } from '../../constants/QualityIssues';
import { useReturnReasons, RETURN_REASON_KEYS } from '../../constants/ReturnReasons';
import { DmsReturnViewButton } from "../../components/DmsReturnViewButton/DmsReturnViewButton";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";

export const ReturnQualityPage = () => {
    const QualityIssueOptions = useQualityIssueOptions();
    const ReturnReasons = useReturnReasons();
    const table = useRef(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [formData, setFormData] = useState({
        status: null,
        sap_tracking_code: "",
        return_reason_pk: null,
        reason_details: "",
        reason_details_text: ""
    });
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState({ evidencia: null, laudo: null });
    const [existingFiles, setExistingFiles] = useState([]);
    const [, setLoadingFiles] = useState(false);
    const [returnReasons, setReturnReasons] = useState([]);

    // Filter status options to only show PENDING (In Progress) and COMPLETED
    const filteredStatusOptions = getReturnStatusOptions().filter(
        opt => opt.value === ReturnStatusEnum.PENDING || opt.value === ReturnStatusEnum.COMPLETED
    )

    // Load return reasons from backend
    React.useEffect(() => {
        const loadReturnReasons = async () => {
            try {
                const response = await fetchData('/return_reason/all', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                    setReturnReasons(response.data.data);
                }
            } catch (error) {
                console.error("Failed to load return reasons:", error);
            }
        };
        loadReturnReasons();
    }, []);

    const loadExistingFiles = useCallback(async (returnPk) => {
        setLoadingFiles(true);
        try {
            const response = await fetchData(`/return/${returnPk}/complete`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.files) {
                setExistingFiles(response.data.data.files);
            }
        } catch (error) {
            console.error("Failed to load existing files:", error);
        } finally {
            setLoadingFiles(false);
        }
    }, []);

    const handleOpenEdit = useCallback((row) => {
        setSelectedReturn(row);
        setFormData({
            status: row.status,
            sap_tracking_code: row.sap_tracking_code || "",
            return_reason_pk: row.return_reason_pk || null,
            reason_details: row.reason_details || "",
            reason_details_text: row.reason_details_text || ""
        });
        setFiles({ evidencia: null, laudo: null });
        setExistingFiles([]);
        loadExistingFiles(row.pk);
        setEditModalOpen(true);
    }, [loadExistingFiles]);

    const handleCloseEdit = useCallback(() => {
        setEditModalOpen(false);
        setSelectedReturn(null);
        setFormData({
            status: null,
            sap_tracking_code: "",
            return_reason_pk: null,
            reason_details: "",
            reason_details_text: ""
        });
        setFiles({ evidencia: null, laudo: null });
        setExistingFiles([]);
    }, []);

    const handleFileSelect = useCallback((docName, file) => {
        setFiles(prev => ({ ...prev, [docName]: file }));
    }, []);

    // Check if required documents are present (new upload or existing)
    const hasRequiredDocuments = useCallback(() => {
        const hasEvidencia = files.evidencia || existingFiles.some(f => f.document_name === 'evidencia');
        const hasLaudo = files.laudo || existingFiles.some(f => f.document_name === 'laudo');
        return hasEvidencia && hasLaudo;
    }, [files, existingFiles]);

    // Check if completion requires documents
    const isCompletingReturn = formData.status === ReturnStatusEnum.COMPLETED;
    const needsDocuments = isCompletingReturn && !hasRequiredDocuments();

    const uploadFiles = useCallback(async () => {
        const uploadedFiles = [];
        for (const [docName, file] of Object.entries(files)) {
            if (file) {
                const response = await fetchUrl(
                    '/return/quality/file_upload',
                    'POST',
                    true,
                    file,
                    false,
                    true,
                    null,
                    null,
                    'multipart/form-data'
                );
                if (response.status === HTTP_STATUS.CREATED_201 && response.data?.data?.pk) {
                    uploadedFiles.push({
                        file_pk: response.data.data.pk,
                        document_name: docName
                    });
                }
            }
        }
        return uploadedFiles;
    }, [files]);

    const handleSave = useCallback(async () => {
        if (!selectedReturn) return;

        // Validate required documents when completing
        if (formData.status === ReturnStatusEnum.COMPLETED && !hasRequiredDocuments()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("RequiredDocuments").d("Required Documents"),
                text: intl.get("EvidenciaAndLaudoRequired").d("Evidence and Report documents are required to complete the return."),
            });
            return;
        }

        const result = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmUpdate").d("Confirm Update"),
            text: intl.get("ConfirmUpdateReturnQuality").d("Are you sure you want to update this return?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            // Upload any new files first
            const uploadedFiles = await uploadFiles();

            // Send all data together in single request to quality endpoint
            const requestBody = {
                files: uploadedFiles,
                status_value: formData.status,
                sap_tracking_code: formData.sap_tracking_code || null,
                return_reason_pk: formData.return_reason_pk || null,
                reason_details: formData.reason_details || null,
                reason_details_text: formData.reason_details_text || null
            };

            const response = await fetchData(
                `/return/${selectedReturn.pk}/quality`,
                "PUT",
                true,
                requestBody
            );

            if (response.status === HTTP_STATUS.OK_200) {
                await Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("ReturnUpdatedSuccessfully").d("Return updated successfully"),
                });
                handleCloseEdit();
                if (table.current) {
                    table.current.updateTable();
                }
            }
        } catch (error) {
            console.error("Failed to update return:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToUpdateReturn").d("Failed to update return"),
            });
        } finally {
            setSaving(false);
        }
    }, [selectedReturn, formData, handleCloseEdit, hasRequiredDocuments, uploadFiles]);

    // Prepare return reason options for select filter
    const reasonOptions = returnReasons.map(reason => ({
        value: reason.name,
        label: ReturnReasons[reason.name] || reason.name
    }));

    // Prepare status options for select filter
    const statusOptions = filteredStatusOptions.map(opt => ({
        value: opt.value,
        label: opt.label
    }));

    const header = [
        {
            label: intl.get("ReturnReason").d("Return Reason"),
            column_name: "return_reason_name",
            filter: true,
            control: "select",
            options: reasonOptions,
            element: (row) => {
                // Get translated return reason name
                const reasonName = row?.return_reason?.name || row?.return_reason_name;
                if (reasonName && ReturnReasons[reasonName]) {
                    return ReturnReasons[reasonName];
                }
                return reasonName || '-';
            }
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "quantity",
            filter: true
        },
        {
            label: intl.get("ReasonDetails").d("Reason Details"),
            column_name: "reason_details",
            filter: true,
            element: (row) => {
                const option = QualityIssueOptions.find(opt => opt.value === row?.reason_details);
                return option ? option.label : row?.reason_details || '-';
            }
        },
        {
            label: intl.get("AdditionalDetails").d("Additional Details"),
            column_name: "reason_details_text",
            filter: true,
            element: (row) => row?.reason_details_text || '-'
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            filter: true,
            control: "select",
            options: statusOptions,
            element: (row) => (
                <Chip
                    label={ReturnStatus.getText(row.status)}
                    color={ReturnStatus.getColor(row.status)}
                    size="small"
                />
            )
        },
        {
            label: intl.get("InvoiceNumber").d("Invoice Number"),
            column_name: "invoice_number",
            filter: true
        },
        {
            label: intl.get("SAPCRMCode").d("SAP CRM Code"),
            column_name: "sap_tracking_code",
            filter: true,
            element: (row) => row?.sap_tracking_code || '-'
        },
        {
            label: intl.get("CreatedOn").d("Created On"),
            column_name: "created_on",
            control: "date",
            filter: true,
            element: (row) => new Date(row.created_on).toLocaleDateString('pt-BR')
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsReturnViewButton row={row} />
                    <DmsPermissions permissions={[Permissions.RETURN_QUALITY_MANAGE]}>
                        <Tooltip title={intl.get("EditQuality").d("Edit Quality")}>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(row)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </DmsPermissions>
                </DmsButtonGroup>
            )
        },
    ];

    return (
        <DmsPage name={intl.get("ReturnQuality").d("Return Quality")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                        {intl.get("ReturnQualityReview").d("Return Quality Review")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("ReturnQualityReviewDescription").d("Review returns with product quality issues. Update status and SAP CRM tracking code.")}
                        </Typography>
                    </Box>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={table}
                        url={'/return/quality/table'}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <DmsModal
                isVisible={editModalOpen}
                onClose={handleCloseEdit}
            >
                <DmsModal.Header title={intl.get("EditReturnQuality").d("Edit Return Quality")} onClose={handleCloseEdit} />
                <DmsModal.Body>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 500 }}>
                        <DmsSelect
                            label={intl.get("ReturnReason").d("Return Reason")}
                            value={formData.return_reason_pk}
                            onChange={(e) => setFormData(prev => ({ ...prev, return_reason_pk: e }))}
                            options={returnReasons.map(reason => ({
                                value: reason.pk,
                                label: ReturnReasons[reason.name] || reason.name
                            }))}
                        />

                        {returnReasons.find(r => r.pk === formData.return_reason_pk)?.name === RETURN_REASON_KEYS.QUALITY_ISSUE && (
                            <DmsSelect
                                label={intl.get("ReasonDetails").d("Reason Details")}
                                value={formData.reason_details}
                                onChange={(e) => setFormData(prev => ({ ...prev, reason_details: e }))}
                                options={QualityIssueOptions}
                            />
                        )}

                        <DmsTextField
                            label={intl.get("AdditionalDetails").d("Additional Details")}
                            value={formData.reason_details_text}
                            onChange={(e) => setFormData(prev => ({ ...prev, reason_details_text: e.target.value }))}
                            multiline
                            rows={3}
                        />

                        <DmsSelect
                            label={intl.get("Status").d("Status")}
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e }))}
                            options={filteredStatusOptions}
                        />

                        <DmsTextField
                            label={intl.get("SAPCRMCode").d("SAP CRM Code")}
                            value={formData.sap_tracking_code}
                            onChange={(e) => setFormData(prev => ({ ...prev, sap_tracking_code: e.target.value }))}
                            placeholder={intl.get("EnterSAPCRMCode").d("Enter SAP CRM code")}
                        />

                        {/* Required Documents Section */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {intl.get("Evidencia").d("Evidence")}
                                {isCompletingReturn && (
                                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                                )}
                            </Typography>
                            {needsDocuments && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {intl.get("DocumentsRequiredToComplete").d("Evidence and Report documents are required to complete the return.")}
                                </Alert>
                            )}
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <DocumentUploadSlot
                                        docName="evidencia"
                                        file={files.evidencia}
                                        onFileSelect={handleFileSelect}
                                        existingFile={existingFiles.find(f => f.document_name === 'evidencia')}
                                        error={needsDocuments && !files.evidencia && !existingFiles.some(f => f.document_name === 'evidencia')}
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {intl.get("Doc.Evidencia").d("Evidence 1")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DocumentUploadSlot
                                        docName="laudo"
                                        file={files.laudo}
                                        onFileSelect={handleFileSelect}
                                        existingFile={existingFiles.find(f => f.document_name === 'laudo')}
                                        error={needsDocuments && !files.laudo && !existingFiles.some(f => f.document_name === 'laudo')}
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {intl.get("Laudo").d("Report")}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleCloseEdit}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={intl.get("Save").d("Save")}
                        onClick={handleSave}
                        disabled={saving || needsDocuments}
                    />
                </DmsModal.Footer>
            </DmsModal>
        </DmsPage>
    );
};

export default ReturnQualityPage;
